import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type InputHTMLAttributes, type ReactNode, useEffect, useImperativeHandle, useRef } from 'react'
import { cn } from '../../lib/utils'

const inputVariants = cva(
  'flex w-full rounded-md border p-4 cursor-text font-number focus:outline-none disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        default:
          'text-header bg-input-bg border-border placeholder:text-secondary hover:border-secondary focus:border-secondary',
        inverse:
          'text-header-inverse autofill-text-header-inverse bg-input-bg-inverse border-border-inverse placeholder:text-secondary-inverse hover:border-secondary-inverse focus:border-secondary-inverse',
      },
      size: {
        default: 'h-14 text-base', // text-base 16px height 56px
        small: 'h-[46px] px-4 py-2 text-base', // text-base 16px height 46px
        large: 'h-[70px] p-6 text-xl', // text-xl 24px height 70px
        xl: 'font-header font-bold h-[98px] p-6 text-[2.5rem]', // text-[2.5rem] 40px height 98px
      },
      weight: {
        default: '',
        normal: 'font-normal',
        semibold: 'font-semibold',
        bold: 'font-bold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      weight: 'default',
    },
  },
)

type InputVariants = typeof inputVariants

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<InputVariants> {
  iconLeft?: ReactNode
  iconRight?: ReactNode
  onBlur?: (args: React.FocusEvent<HTMLInputElement>) => void
  shouldFocus?: boolean
  inputClassName?: string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, variant, weight, size, type, iconLeft, iconRight, onBlur, shouldFocus, inputClassName, ...props },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => {
      if (!inputRef.current) {
        throw new Error('Input ref is not assigned')
      }
      return inputRef.current
    })

    useEffect(() => {
      if (shouldFocus && inputRef.current) {
        inputRef.current.focus()
      }
    }, [shouldFocus])

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event)
    }

    return (
      <div className={cn('flex relative items-center', className)}>
        {iconLeft && (
          <div data-left-icon className="absolute left-4">
            {iconLeft}
          </div>
        )}
        <input
          type={type}
          className={cn(
            inputVariants({
              variant,
              size,
              weight,
            }),
            iconLeft && 'pl-12',
            iconRight && 'pr-12',
            inputClassName,
          )}
          onBlur={handleBlur}
          ref={inputRef}
          {...props}
        />
        {iconRight && (
          <div data-right-icon className="absolute right-4">
            {iconRight}
          </div>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input }
